import { useEffect, useState, useRef } from 'react';
import apiInstance from '../../api/api';
import { useSearchParams } from "react-router-dom";
import AuthLayout from "../../components/AuthLayout/AuthLayout";

import { API_URL } from "../../configs";

const ReportTagsManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Main state
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalFound, setTotalFound] = useState(0);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [issueFilter, setIssueFilter] = useState('');
  const [minUsage, setMinUsage] = useState('');
  const [maxUsage, setMaxUsage] = useState('');
  
  // UI state
  const [selectedTag, setSelectedTag] = useState(null);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [tagToRemove, setTagToRemove] = useState(null);
  const [resourcesData, setResourcesData] = useState(null);
  const [loadingResources, setLoadingResources] = useState(false);
  
  // Remove modal state
  const [dryRun, setDryRun] = useState(true);
  const [removingTag, setRemovingTag] = useState(false);
  const [removalResult, setRemovalResult] = useState(null);
  
  // Tag selection and operations state
  const [selectedTags, setSelectedTags] = useState([]); // array of tag objects
  const [showMerge, setShowMerge] = useState(false);
  const [primaryTag, setPrimaryTag] = useState("");
  const [mergeMsg, setMergeMsg] = useState("");
  const [mergeErr, setMergeErr] = useState(null);
  
  // Replace tag state
  const [showReplace, setShowReplace] = useState(false);
  const [replaceOldTag, setReplaceOldTag] = useState("");
  const [replaceNewTag, setReplaceNewTag] = useState("");
  const [replaceMsg, setReplaceMsg] = useState("");
  const [replaceErr, setReplaceErr] = useState(null);
  
  // Tag expansion state
  const [showExpansion, setShowExpansion] = useState(false);
  const [selectedTagForExpansion, setSelectedTagForExpansion] = useState(null);
  const [expansionTerms, setExpansionTerms] = useState("");
  const [currentExpansions, setCurrentExpansions] = useState([]);
  const [expansionMsg, setExpansionMsg] = useState("");
  const [expansionErr, setExpansionErr] = useState(null);
  const [loadingExpansions, setLoadingExpansions] = useState(false);
  
  // Messages
  const [successMsg, setSuccessMsg] = useState('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const limit = 50;

  // Initialize state from URL parameters
  const initializeFromURL = () => {
    const urlSearchQuery = searchParams.get('q') || '';
    const urlIssueFilter = searchParams.get('issue') || '';
    const urlMinUsage = searchParams.get('min_usage') || '';
    const urlMaxUsage = searchParams.get('max_usage') || '';
    const urlPage = parseInt(searchParams.get('page')) || 1;

    setSearchQuery(urlSearchQuery);
    setIssueFilter(urlIssueFilter);
    setMinUsage(urlMinUsage);
    setMaxUsage(urlMaxUsage);
    setPage(urlPage);

    return { urlSearchQuery, urlIssueFilter, urlMinUsage, urlMaxUsage, urlPage };
  };

  // Update URL parameters
  const updateURL = (params) => {
    const newSearchParams = new URLSearchParams();
    
    if (params.q) newSearchParams.set('q', params.q);
    if (params.issue) newSearchParams.set('issue', params.issue);
    if (params.min_usage) newSearchParams.set('min_usage', params.min_usage);
    if (params.max_usage) newSearchParams.set('max_usage', params.max_usage);
    if (params.page && params.page > 1) newSearchParams.set('page', params.page);

    setSearchParams(newSearchParams);
  };

  // API Base URL for tags (updated to match main.py routing)
  const TAGS_API_BASE = `${API_URL}datas/tags`;

  // Fetch tags with search functionality
  const fetchTags = async (query = '', issue = '', minUsage = '', maxUsage = '', pageNum = 1) => {
    setLoading(true);
    setError(null);
    setSuccessMsg('');
    
    try {
      let url = '';
      let params = {};
      
      if (query) {
        // Use search endpoint
        url = `${TAGS_API_BASE}/search`;
        params = {
          q: query,
          limit: limit,
          offset: (pageNum - 1) * limit
        };
      } else if (issue || minUsage || maxUsage) {
        // Use analysis endpoint
        url = `${TAGS_API_BASE}/analysis`;
        params = {
          issue: issue || undefined,
          min_usage: minUsage ? parseInt(minUsage) : 0,
          max_usage: maxUsage ? parseInt(maxUsage) : undefined,
          limit: limit,
          offset: (pageNum - 1) * limit
        };
      } else {
        // Default: get all tags using analysis without filters
        url = `${TAGS_API_BASE}/analysis`;
        params = {
          min_usage: 0,
          limit: limit,
          offset: (pageNum - 1) * limit
        };
      }

      const response = await apiInstance.get(url, { params });

      if (response.status === 200) {
        setTags(response.data.tags || []);
        setTotalFound(response.data.total_found || 0);
      } else {
        setTags([]);
        setTotalFound(0);
      }
    } catch (err) {
      console.error("Error fetching tags:", err);
      setError(`Failed to fetch tags: ${err.response?.data?.detail || err.message}`);
      setTags([]);
      setTotalFound(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch resources for a specific tag
  const fetchTagResources = async (tagSlug) => {
    setLoadingResources(true);
    try {
      const response = await apiInstance.get(`${TAGS_API_BASE}/${tagSlug}/resources`, {
        params: { limit: 100 }
      });
      
      if (response.status === 200) {
        setResourcesData(response.data);
      }
    } catch (err) {
      console.error(`Error fetching resources for tag ${tagSlug}:`, err);
      setError(`Failed to load resources: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoadingResources(false);
    }
  };

  // Remove tag with dry-run preview
  const removeTag = async (tagSlug, dryRun = true) => {
    try {
      const response = await apiInstance.delete(`${TAGS_API_BASE}/${tagSlug}`, {
        params: { dry_run: dryRun }
      });
      
      if (response.status === 200) {
        return response.data;
      }
      return null;
    } catch (err) {
      console.error(`Error removing tag ${tagSlug}:`, err);
      throw err;
    }
  };

  // Handle search form submission
  const handleFilterSubmit = (event) => {
    event.preventDefault();
    const newPage = 1;
    setPage(newPage);
    
    // Update URL with new filters
    updateURL({
      q: searchQuery,
      issue: issueFilter,
      min_usage: minUsage,
      max_usage: maxUsage,
      page: newPage
    });
    
    fetchTags(searchQuery, issueFilter, minUsage, maxUsage, newPage);
  };

  // Handle tag selection for resource preview
  const handleTagResourceView = (tag) => {
    setSelectedTag(tag);
    setShowResourceModal(true);
    fetchTagResources(tag.slug || tag._id);
  };

  // Handle tag removal initiation
  const handleRemoveTag = (tag) => {
    setTagToRemove(tag);
    setShowRemoveModal(true);
    setDryRun(true); // Reset to dry run by default
    setRemovalResult(null); // Clear previous results
  };

  // Handle successful tag removal
  const handleTagRemoved = () => {
    setShowRemoveModal(false);
    setTagToRemove(null);
    setRemovalResult(null);
    setSuccessMsg('Tag removed successfully');
    // Refresh tags list with current page and maintain URL state
    fetchTags(searchQuery, issueFilter, minUsage, maxUsage, page);
  };

  // Handle confirm remove in modal
  const handleConfirmRemove = async () => {
    if (!tagToRemove) return;
    
    setRemovingTag(true);
    setRemovalResult(null);
    
    try {
      const result = await removeTag(tagToRemove.slug || tagToRemove._id, dryRun);
      setRemovalResult(result);
      
      if (result.success && !dryRun) {
        // Close modal and refresh if actual removal was successful
        setTimeout(() => {
          handleTagRemoved();
        }, 2000);
      }
    } catch (error) {
      setRemovalResult({
        success: false,
        message: error.message || 'Failed to remove tag'
      });
    } finally {
      setRemovingTag(false);
    }
  };

  // Handle pagination
  const handleNextPage = () => {
    const newPage = page + 1;
    if (newPage <= Math.ceil(totalFound / limit)) {
      setPage(newPage);
      
      // Update URL with new page
      updateURL({
        q: searchQuery,
        issue: issueFilter,
        min_usage: minUsage,
        max_usage: maxUsage,
        page: newPage
      });
      
      fetchTags(searchQuery, issueFilter, minUsage, maxUsage, newPage);
    }
  };

  const handlePreviousPage = () => {
    const newPage = page - 1;
    if (newPage >= 1) {
      setPage(newPage);
      
      // Update URL with new page
      updateURL({
        q: searchQuery,
        issue: issueFilter,
        min_usage: minUsage,
        max_usage: maxUsage,
        page: newPage
      });
      
      fetchTags(searchQuery, issueFilter, minUsage, maxUsage, newPage);
    }
  };

  const handlePageClick = (pageNum) => {
    setPage(pageNum);
    
    // Update URL with new page
    updateURL({
      q: searchQuery,
      issue: issueFilter,
      min_usage: minUsage,
      max_usage: maxUsage,
      page: pageNum
    });
    
    fetchTags(searchQuery, issueFilter, minUsage, maxUsage, pageNum);
  };

  // Handle input changes
  const handleSearchQueryChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleIssueFilterChange = (event) => {
    setIssueFilter(event.target.value);
  };

  const handleMinUsageChange = (event) => {
    setMinUsage(event.target.value);
  };

  const handleMaxUsageChange = (event) => {
    setMaxUsage(event.target.value);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setIssueFilter('');
    setMinUsage('');
    setMaxUsage('');
    setPage(1);
    
    // Clear URL parameters
    setSearchParams(new URLSearchParams());
    
    fetchTags('', '', '', '', 1);
  };

  // Handle tag selection for merge/replace operations
  const handleTagSelect = (tag) => {
    setSelectedTags(prev => {
      const tagSlug = tag.slug || tag._id;
      const isSelected = prev.some(t => (t.slug || t._id) === tagSlug);
      
      if (isSelected) {
        return prev.filter(t => (t.slug || t._id) !== tagSlug);
      } else {
        return [...prev, tag];
      }
    });
  };

  // Handle show merge modal
  const handleShowMerge = () => {
    if (selectedTags.length < 2) return;
    
    setShowMerge(true);
    setMergeMsg("");
    setMergeErr(null);
    setPrimaryTag(selectedTags[0].slug || selectedTags[0]._id);
  };

  // Handle merge tags submission
  const handleMergeTagsSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMergeErr(null);
    setMergeMsg("");
    
    if (!primaryTag || selectedTags.length < 2) {
      setMergeErr("Select at least two tags and a primary tag.");
      return;
    }

    try {
      const oldTags = selectedTags
        .map(t => t.slug || t._id)
        .filter(slug => slug !== primaryTag);

      const response = await apiInstance.put(`${API_URL}resources/tags/replace`, {
        old_tags: oldTags,
        new_tag: primaryTag,
      });

      setMergeMsg(response.data.message || "Tags merged successfully");
      setSuccessMsg("Tags merged successfully");
      
      setTimeout(() => {
        setShowMerge(false);
        setSelectedTags([]);
        setPrimaryTag("");
        // Refresh tags list
        fetchTags(searchQuery, issueFilter, minUsage, maxUsage, page);
      }, 1500);

    } catch (err) {
      if (err.response) {
        setMergeErr(err.response.data.detail);
      } else {
        setMergeErr("An error occurred while merging tags.");
      }
    }
  };

  // Handle show replace modal
  const handleShowReplace = () => {
    setShowReplace(true);
    setReplaceMsg("");
    setReplaceErr(null);
    setReplaceOldTag("");
    setReplaceNewTag("");
  };

  // Handle replace tag submission
  const handleReplaceTagSubmit = async (e) => {
    e.preventDefault();
    setReplaceErr(null);
    setReplaceMsg("");

    if (!replaceOldTag || !replaceNewTag) {
      setReplaceErr("Both old and new tag names are required.");
      return;
    }

    try {
      const response = await apiInstance.put(`${API_URL}resources/tags/replace`, {
        old_tag: replaceOldTag,
        new_tag: replaceNewTag,
      });

      setReplaceMsg(response.data.message || "Tag replaced successfully");
      setSuccessMsg("Tag replaced successfully");
      
      setTimeout(() => {
        setShowReplace(false);
        setReplaceOldTag("");
        setReplaceNewTag("");
        // Refresh tags list
        fetchTags(searchQuery, issueFilter, minUsage, maxUsage, page);
      }, 1500);

    } catch (err) {
      if (err.response) {
        setReplaceErr(err.response.data.detail);
      } else {
        setReplaceErr("An error occurred while replacing tag.");
      }
    }
  };

  // Handle show expansion modal
  const handleShowExpansion = (tag) => {
    setSelectedTagForExpansion(tag);
    setShowExpansion(true);
    setExpansionMsg("");
    setExpansionErr(null);
    setExpansionTerms("");
    
    // Fetch current expansions
    fetchTagExpansions(tag.slug || tag._id);
  };

  // Fetch current expansions for a tag
  const fetchTagExpansions = async (tagSlug) => {
    setLoadingExpansions(true);
    try {
      const response = await apiInstance.get(`${TAGS_API_BASE}/${tagSlug}/expansions`);
      if (response.status === 200) {
        setCurrentExpansions(response.data.expanded_terms || []);
      }
    } catch (err) {
      console.error(`Error fetching expansions for tag ${tagSlug}:`, err);
      setCurrentExpansions([]);
    } finally {
      setLoadingExpansions(false);
    }
  };

  // Handle add expansion submission
  const handleAddExpansionSubmit = async (e) => {
    e.preventDefault();
    setExpansionErr(null);
    setExpansionMsg("");

    if (!expansionTerms.trim()) {
      setExpansionErr("Please enter expansion terms.");
      return;
    }

    // Parse expansion terms (comma-separated)
    const termsArray = expansionTerms
      .split(',')
      .map(term => term.trim())
      .filter(term => term.length > 0);

    if (termsArray.length === 0) {
      setExpansionErr("Please enter valid expansion terms separated by commas.");
      return;
    }

    try {
      const tagSlug = selectedTagForExpansion.slug || selectedTagForExpansion._id;
      const response = await apiInstance.post(`${TAGS_API_BASE}/${tagSlug}/expand`, null, {
        params: { expanded_terms: termsArray }
      });

      setExpansionMsg(response.data.message || "Expansion terms added successfully");
      setSuccessMsg("Tag expansion added successfully");
      
      // Refresh current expansions
      fetchTagExpansions(tagSlug);
      setExpansionTerms("");

      setTimeout(() => {
        setExpansionMsg("");
      }, 3000);

    } catch (err) {
      if (err.response) {
        setExpansionErr(err.response.data.detail);
      } else {
        setExpansionErr("An error occurred while adding expansion terms.");
      }
    }
  };

  // Initial data fetch
  useEffect(() => {
    const urlParams = initializeFromURL();
    fetchTags(urlParams.urlSearchQuery, urlParams.urlIssueFilter, urlParams.urlMinUsage, urlParams.urlMaxUsage, urlParams.urlPage);
  }, []);

  // Refetch data when URL parameters change (for browser back/forward)
  useEffect(() => {
    const urlSearchQuery = searchParams.get('q') || '';
    const urlIssueFilter = searchParams.get('issue') || '';
    const urlMinUsage = searchParams.get('min_usage') || '';
    const urlMaxUsage = searchParams.get('max_usage') || '';
    const urlPage = parseInt(searchParams.get('page')) || 1;

    // Only update if URL params differ from current state
    if (urlSearchQuery !== searchQuery || 
        urlIssueFilter !== issueFilter || 
        urlMinUsage !== minUsage || 
        urlMaxUsage !== maxUsage || 
        urlPage !== page) {
      
      setSearchQuery(urlSearchQuery);
      setIssueFilter(urlIssueFilter);
      setMinUsage(urlMinUsage);
      setMaxUsage(urlMaxUsage);
      setPage(urlPage);
      
      fetchTags(urlSearchQuery, urlIssueFilter, urlMinUsage, urlMaxUsage, urlPage);
    }
  }, [searchParams]);

  return (
    <AuthLayout>
      <header className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fs-5">
          Tags Management 
          <span className="badge bg-info text-white ms-2">Phase 1</span>
        </h2>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={handleShowReplace}
            title="Replace tag"
          >
            <i className="bi bi-arrow-left-right"></i> Replace
          </button>
          <button
            className="btn btn-outline-primary btn-sm"
            disabled={selectedTags.length < 2}
            onClick={handleShowMerge}
            title="Merge selected tags"
          >
            <i className="bi bi-union"></i> Merge ({selectedTags.length})
          </button>
        </div>
      </header>
      
      <div>
        {/* Messages */}
        {error && <div className="alert alert-danger my-2">{error}</div>}
        {successMsg && <div className="alert alert-success my-2">{successMsg}</div>}
        {mergeMsg && <div className="alert alert-success my-2">{mergeMsg}</div>}
        {mergeErr && <div className="alert alert-danger my-2">{mergeErr}</div>}
        {replaceMsg && <div className="alert alert-success my-2">{replaceMsg}</div>}
        {expansionMsg && <div className="alert alert-success my-2">{expansionMsg}</div>}
        {expansionErr && <div className="alert alert-danger my-2">{expansionErr}</div>}

        {/* Search and Filter Controls */}
        <div className="d-flex justify-content-start align-items-center mb-3">
          <form className="d-flex align-items-center gap-2" style={{ maxWidth: 700 }} onSubmit={handleFilterSubmit}>
            <input
              type="text"
              className="form-control"
              placeholder="Search tags..."
              value={searchQuery}
              onChange={handleSearchQueryChange}
              style={{ minWidth: 160 }}
            />
            <select
              className="form-control"
              value={issueFilter}
              onChange={handleIssueFilterChange}
              style={{ width: 150 }}
            >
              <option value="">All Issues</option>
              <option value="blocked">Blocked Tags</option>
              <option value="platform">Platform Terms</option>
              <option value="plural">Plural Forms</option>
              <option value="low-signal">Low Signal</option>
              <option value="special">Special Chars</option>
            </select>
            <input
              type="number"
              className="form-control"
              placeholder="Min usage"
              value={minUsage}
              onChange={handleMinUsageChange}
              style={{ width: 110 }}
              min={0}
            />
            <input
              type="number"
              className="form-control"
              placeholder="Max usage"
              value={maxUsage}
              onChange={handleMaxUsageChange}
              style={{ width: 110 }}
              min={0}
            />
            <button className="btn btn-primary" type="submit" disabled={loading}>
              {loading ? 'Loading...' : 'Apply'}
            </button>
            {(searchQuery || issueFilter || minUsage || maxUsage) && (
              <button 
                className="btn btn-outline-secondary" 
                type="button" 
                onClick={handleClearFilters}
                disabled={loading}
              >
                Clear
              </button>
            )}
          </form>
        </div>

        {/* Stats Summary */}
        {!loading && (
          <div className="mb-3">
            <span className="badge bg-light text-muted">
              {totalFound} tags found
              {searchQuery && ` matching "${searchQuery}"`}
              {issueFilter && ` with issue "${issueFilter}"`}
              {(minUsage || maxUsage) && ` (usage: ${minUsage || 0}-${maxUsage || '∞'})`}
              {totalFound > limit && ` • Showing page ${page} of ${Math.ceil(totalFound / limit)}`}
            </span>
          </div>
        )}

        {/* Tags Table */}
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading tags...</p>
          </div>
        ) : tags.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-striped align-middle">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={selectedTags.length === tags.length && tags.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTags([...tags]);
                        } else {
                          setSelectedTags([]);
                        }
                      }}
                    />
                  </th>
                  <th className='fw-normal text-muted'>Tag</th>
                  <th className='fw-normal text-muted'>Usage</th>
                  <th className='fw-normal text-muted'>Weight</th>
                  <th className='fw-normal text-muted'>Confidence</th>
                  <th className='fw-normal text-muted'>Issues</th>
                  <th className='fw-normal text-muted'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tags.map((tag, index) => (
                  <tr key={tag.slug || tag._id || index}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedTags.some(t => (t.slug || t._id) === (tag.slug || tag._id))}
                        onChange={() => handleTagSelect(tag)}
                      />
                    </td>
                    <td>
                      {/* <span className="badge bg-light text-dark fw-semibold">
                        {tag.slug || tag._id}
                      </span> */}
                      <span
                            className="badge rounded bg-light small rounded me-2 text-dark fw-semibold fs-6 my-1 py-1 px-3 suggested-tag"
                        > {tag.slug || tag._id}</span> 
                    </td>
                    <td className="text-muted">
                      {tag.usage_count}
                    </td>
                    <td>
                      {tag.avg_weight ? (
                        <span className="badge bg-secondary fw-normal">
                          {tag.avg_weight.toFixed(3)}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {tag.avg_confidence ? (
                        <span className="badge bg-info bg-opacity-25 text-info fw-normal">
                          {(tag.avg_confidence * 100).toFixed(1)}%
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {tag.issues && tag.issues.length > 0 ? (
                        <div>
                          {tag.issues.map(issue => (
                            <span key={issue} className="badge bg-warning bg-opacity-25 text-warning me-1 small text-uppercase fw-normal">
                              {issue}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted"></span>
                      )}
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <button
                          className="btn fs-6 p-1 small mx-1"
                          onClick={() => handleTagResourceView(tag)}
                          title="View resources"
                        >
                          <i className="bi bi-eye d-flex text-muted"></i>
                        </button>
                        <button
                          className="btn fs-6 p-1 small mx-1"
                          onClick={() => handleShowExpansion(tag)}
                          title="Expand tag"
                        >
                          <i className="bi bi-pencil small d-flex text-muted"></i>
                        </button>
                        <button
                          className="btn fs-6 p-1 border-0 small mx-1"
                          onClick={() => handleRemoveTag(tag)}
                          title="Remove tag"
                          disabled={tag.usage_count > 100}
                        >
                          <i className="bi bi-trash2 d-flex text-muted"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No tags found</p>
        )}

        {/* Pagination Controls */}
        {!loading && tags.length > 0 && totalFound > limit && (
          <div className="pagination d-flex align-items-center gap-2">
            <span className="badge bg-light text-muted">
              {totalFound} total • Page {page} of {Math.ceil(totalFound / limit)}
            </span>
            <button 
              className="btn btn-light btn-sm" 
              onClick={handlePreviousPage} 
              disabled={page === 1 || loading}
            >
              &lt;
            </button>
            {(() => {
              const totalPages = Math.max(1, Math.ceil(totalFound / limit));
              const pages = [];
              if (totalPages <= 7) {
                for (let i = 1; i <= totalPages; i++) {
                  pages.push(i);
                }
              } else {
                pages.push(1);
                let start = Math.max(2, page - 1);
                let end = Math.min(totalPages - 1, page + 1);
                if (start > 2) pages.push('...');
                for (let i = start; i <= end; i++) {
                  pages.push(i);
                }
                if (end < totalPages - 1) pages.push('...');
                pages.push(totalPages);
              }
              return pages.map((p, idx) =>
                p === '...'
                  ? <span key={"ellipsis-" + idx} className="px-1">...</span>
                  : <button
                      key={p}
                      className={`btn btn-sm ${page === p ? 'btn-secondary' : 'btn-light'}`}
                      onClick={() => handlePageClick(p)}
                      disabled={loading}
                    >
                      {p}
                    </button>
              );
            })()}
            <button 
              className="btn btn-light btn-sm" 
              onClick={handleNextPage} 
              disabled={page >= Math.ceil(totalFound / limit) || loading}
            >
              &gt;
            </button>
          </div>
        )}
      </div>

      {/* Resource Modal */}
      {showResourceModal && selectedTag && (
        <div
          className="modal fade show"
          tabIndex="-1"
          style={{ display: 'block', background: 'rgba(0,0,0,0.3)' }}
          onClick={() => setShowResourceModal(false)}
        >
          <div className="modal-dialog modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Resources using tag: <span className="text-primary">{selectedTag.slug || selectedTag._id}</span>
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowResourceModal(false)}></button>
              </div>
              <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {loadingResources ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading resources...</span>
                    </div>
                  </div>
                ) : resourcesData && resourcesData.resources ? (
                  <div>
                    <p className="text-muted mb-3">
                      Showing {resourcesData.returned_count} of {resourcesData.total_resources} resources
                    </p>
                    {resourcesData.resources.map((resource, index) => (
                      <div key={resource.resource_id || index} className="border-bottom pb-2 mb-2">
                        <strong>{resource.resource_name}</strong>
                        <br />
                        <small className="text-muted">ID: {resource.resource_id}</small>
                        {resource.tag_weight && (
                          <span className="badge bg-secondary ms-2">Weight: {resource.tag_weight.toFixed(3)}</span>
                        )}
                        {resource.tag_confidence && (
                          <span className="badge bg-info ms-2">Confidence: {(resource.tag_confidence * 100).toFixed(1)}%</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No resources found for this tag.</p>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowResourceModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove Tag Modal */}
      {showRemoveModal && tagToRemove && (
        <div
          className="modal fade show"
          tabIndex="-1"
          style={{ display: 'block', background: 'rgba(0,0,0,0.3)' }}
          onClick={() => setShowRemoveModal(false)}
        >
          <div className="modal-dialog" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Remove Tag: <span className="text-danger">{tagToRemove.slug || tagToRemove._id}</span>
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowRemoveModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <strong>Warning:</strong> This will remove the tag from all {tagToRemove.usage_count} resources.
                </div>
                
                <div className="mb-3">
                  <p><strong>Tag Details:</strong></p>
                  <ul>
                    <li>Slug: {tagToRemove.slug || tagToRemove._id}</li>
                    <li>Usage Count: {tagToRemove.usage_count}</li>
                    {tagToRemove.avg_weight && (
                      <li>Average Weight: {tagToRemove.avg_weight.toFixed(3)}</li>
                    )}
                    {tagToRemove.avg_confidence && (
                      <li>Average Confidence: {(tagToRemove.avg_confidence * 100).toFixed(1)}%</li>
                    )}
                    {tagToRemove.issues && tagToRemove.issues.length > 0 && (
                      <li>Issues: {tagToRemove.issues.join(', ')}</li>
                    )}
                  </ul>
                </div>

                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="dryRunCheck"
                    checked={dryRun}
                    onChange={(e) => setDryRun(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="dryRunCheck">
                    Dry run (preview changes without applying)
                  </label>
                </div>

                {removalResult && (
                  <div className={`alert ${removalResult.success ? 'alert-success' : 'alert-danger'}`}>
                    <strong>{removalResult.success ? 'Success:' : 'Error:'}</strong> {removalResult.message}
                    {removalResult.affected_count !== undefined && (
                      <p className="mb-0 mt-2">Resources that would be affected: {removalResult.affected_count}</p>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowRemoveModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn ${dryRun ? 'btn-info' : 'btn-danger'}`}
                  onClick={handleConfirmRemove}
                  disabled={removingTag}
                >
                  {removingTag ? 'Processing...' : (dryRun ? 'Preview Remove' : 'Confirm Remove')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Merge Tags Modal */}
      {showMerge && (
        <div
          className="modal fade show"
          tabIndex="-1"
          style={{ display: 'block', background: 'rgba(0,0,0,0.3)' }}
          onClick={() => setShowMerge(false)}
        >
          <div className="modal-dialog" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <form onSubmit={handleMergeTagsSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">Merge Tags</h5>
                  <button type="button" className="btn-close" onClick={() => setShowMerge(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <p><strong>Select primary tag</strong> (all others will be merged into this):</p>
                    <div className="mb-3">
                      {selectedTags.map(tag => {
                        const tagSlug = tag.slug || tag._id;
                        return (
                          <label key={tagSlug} className="me-3 d-block mb-2">
                            <input
                              type="radio"
                              name="primaryTag"
                              value={tagSlug}
                              checked={primaryTag === tagSlug}
                              onChange={() => setPrimaryTag(tagSlug)}
                              className="form-check-input me-2"
                            />
                            <span className="badge bg-secondary text-white">
                              {tagSlug} ({tag.usage_count} resources)
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  {mergeMsg && <div className="alert alert-success">{mergeMsg}</div>}
                  {mergeErr && <div className="alert alert-danger">{mergeErr}</div>}
                  <div className="text-muted mt-2" style={{ fontSize: '0.9em' }}>
                    All selected tags will be merged into the primary tag. Their resources will be updated, and old tags will be removed.
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowMerge(false)}>
                    Cancel
                  </button>
                  <button 
                    className="btn btn-primary" 
                    type="submit" 
                    disabled={selectedTags.length < 2 || !primaryTag}
                  >
                    Merge Tags
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Replace Tag Modal */}
      {showReplace && (
        <div
          className="modal fade show"
          tabIndex="-1"
          style={{ display: 'block', background: 'rgba(0,0,0,0.3)' }}
          onClick={() => setShowReplace(false)}
        >
          <div className="modal-dialog" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <form onSubmit={handleReplaceTagSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">Replace Tag</h5>
                  <button type="button" className="btn-close" onClick={() => setShowReplace(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="oldTag" className="form-label">Old Tag Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="oldTag"
                      placeholder="Enter old tag name"
                      value={replaceOldTag}
                      onChange={(e) => setReplaceOldTag(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="newTag" className="form-label">New Tag Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="newTag"
                      placeholder="Enter new tag name"
                      value={replaceNewTag}
                      onChange={(e) => setReplaceNewTag(e.target.value)}
                      required
                    />
                  </div>
                  {replaceMsg && <div className="alert alert-success">{replaceMsg}</div>}
                  {replaceErr && <div className="alert alert-danger">{replaceErr}</div>}
                  <div className="text-muted mt-2" style={{ fontSize: '0.9em' }}>
                    All resources with the old tag will be updated to use the new tag name.
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowReplace(false)}>
                    Cancel
                  </button>
                  <button 
                    className="btn btn-primary" 
                    type="submit" 
                    disabled={!replaceOldTag || !replaceNewTag}
                  >
                    Replace Tag
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Tag Expansion Modal */}
      {showExpansion && selectedTagForExpansion && (
        <div
          className="modal fade show"
          tabIndex="-1"
          style={{ display: 'block', background: 'rgba(0,0,0,0.3)' }}
          onClick={() => setShowExpansion(false)}
        >
          <div className="modal-dialog modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Expand Tag: <span className="text-primary">{selectedTagForExpansion.slug || selectedTagForExpansion._id}</span>
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowExpansion(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <p><strong>Add expanded terms for this tag:</strong></p>
                  <small className="text-muted">
                    Expanded terms help find resources that use related concepts. For example, "background" could be expanded with "bg, scene, backdrop, environment".
                  </small>
                </div>

                <form onSubmit={handleAddExpansionSubmit}>
                  <div className="mb-3">
                    <label htmlFor="expansionTerms" className="form-label">Expansion Terms</label>
                    <input
                      type="text"
                      className="form-control"
                      id="expansionTerms"
                      placeholder="bg, scene, backdrop, environment (comma-separated)"
                      value={expansionTerms}
                      onChange={(e) => setExpansionTerms(e.target.value)}
                      required
                    />
                    <small className="form-text text-muted">Enter multiple terms separated by commas</small>
                  </div>
                  <button type="submit" className="btn btn-primary">Add Expansion Terms</button>
                </form>

                {/* Current Expansions */}
                <div className="mt-4">
                  <h6>Current Expanded Terms:</h6>
                  {loadingExpansions ? (
                    <div className="text-center py-2">
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : currentExpansions.length > 0 ? (
                    <div className="mt-2">
                      {currentExpansions.map((term, index) => (
                        <span key={index} className="badge bg-secondary me-2 mb-1">
                          {term}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">No expanded terms currently set for this tag.</p>
                  )}
                </div>

                {/* Example for "background" tag */}
                {(selectedTagForExpansion.slug === 'background' || selectedTagForExpansion._id === 'background') && (
                  <div className="mt-3 p-3 bg-light rounded">
                    <h6 className="text-primary">💡 Suggested expansions for "background":</h6>
                    <div className="mt-2">
                      <span className="badge bg-outline-primary me-2">bg</span>
                      <span className="badge bg-outline-primary me-2">scene</span>
                      <span className="badge bg-outline-primary me-2">backdrop</span>
                      <span className="badge bg-outline-primary me-2">environment</span>
                      <span className="badge bg-outline-primary me-2">setting</span>
                      <span className="badge bg-outline-primary me-2">scenery</span>
                    </div>
                    <small className="text-muted d-block mt-1">Copy: bg, scene, backdrop, environment, setting, scenery</small>
                  </div>
                )}

                {expansionMsg && <div className="alert alert-success mt-3">{expansionMsg}</div>}
                {expansionErr && <div className="alert alert-danger mt-3">{expansionErr}</div>}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowExpansion(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AuthLayout>
  );
};

export default ReportTagsManagement;